import React, { useEffect, useMemo, useState } from "react";
import {
  Users, Crosshair, Shield, Skull, Truck, Flag, MessageSquare, Bot, Radio,
} from "lucide-react";
import { Arma } from "@/lib/armaApi";
import { useInstance } from "@/context/InstanceContext";
import CornerFrame from "@/components/CornerFrame";
import HeroBanner from "@/components/HeroBanner";

function FactionPatch({ faction }) {
  const cls =
    faction === "US" ? "patch-us"
    : faction === "USSR" ? "patch-ussr"
    : faction === "FIA" ? "patch-fia"
    : "patch-muted";
  return <span className={`patch ${cls}`}>{faction || "—"}</span>;
}

function formatTime(iso) {
  if (!iso) return "--:--:--";
  try {
    return new Date(iso).toLocaleTimeString("de-DE", { hour12: false });
  } catch {
    return iso;
  }
}

function cleanPrefab(s) {
  if (!s) return "—";
  return s.replace(/.*[/\\]/, "").replace(/\.\w+$/i, "");
}

function StatTile({ icon: Icon, label, value, sub, accent }) {
  const color =
    accent === "red" ? "var(--red-bright)"
    : accent === "olive" ? "var(--olive-bright)"
    : accent === "amber" ? "var(--amber-bright)"
    : "var(--tan)";
  return (
    <CornerFrame className="tac-panel">
      <div className="px-3 pt-2 pb-3">
        <div className="flex items-center justify-between">
          <div className="label-tech">{label}</div>
          <Icon className="w-3.5 h-3.5" style={{ color }} />
        </div>
        <div className="font-stencil text-4xl mt-1 leading-none tabular-nums" style={{ color: "var(--text)" }}>
          {value}
        </div>
        {sub && (
          <div className="text-[10px] font-mono-tech mt-1.5" style={{ color: "var(--text-muted)" }}>
            {sub}
          </div>
        )}
      </div>
    </CornerFrame>
  );
}

function PanelHeader({ title, icon: Icon, badge, right }) {
  return (
    <div className="tac-panel-header">
      <div className="label-tech flex items-center gap-2" style={{ color: "var(--tan)" }}>
        {Icon && <Icon className="w-3 h-3" />}
        {title}
      </div>
      <div className="flex items-center gap-3">
        {right}
        {badge !== undefined && (
          <div className="label-tech" style={{ color: "var(--text-muted)" }}>{badge}</div>
        )}
      </div>
    </div>
  );
}

const FEED_FILTERS = [
  { id: "all", label: "ALL" },
  { id: "pvp", label: "PVP" },
  { id: "bot", label: "BOT" },
  { id: "ff",  label: "FF"  },
];

export default function Dashboard() {
  const { instance } = useInstance();
  const [summary, setSummary] = useState(null);
  const [feed, setFeed] = useState([]);
  const [chat, setChat] = useState([]);
  const [bases, setBases] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [error, setError] = useState(null);
  const [feedFilter, setFeedFilter] = useState("all");
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [s, f, c, b, v] = await Promise.all([
          Arma.dashboardSummary(instance),
          Arma.killfeed(instance, 20),
          Arma.chat(instance, 30),
          Arma.bases(instance),
          Arma.vehicles(instance),
        ]);
        if (cancelled) return;
        setSummary(s); setFeed(f); setChat(c); setBases(b); setVehicles(v); setError(null);
      } catch (e) {
        if (!cancelled) setError(e?.message || "API-Verbindungsfehler");
      }
    };
    load();
    const id = setInterval(load, 10_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [instance]);

  const sessionStarted = useMemo(
    () => (summary?.sessionStart ? new Date(summary.sessionStart).toLocaleString("de-DE") : "—"),
    [summary?.sessionStart]
  );

  const sessionDuration = useMemo(() => {
    if (!summary?.sessionStart) return null;
    const diffMs = now - new Date(summary.sessionStart).getTime();
    if (diffMs < 0) return null;
    const h = Math.floor(diffMs / 3600000);
    const m = Math.floor((diffMs % 3600000) / 60000);
    const s = Math.floor((diffMs % 60000) / 1000);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [summary?.sessionStart, now]);

  const filteredFeed = useMemo(() => {
    if (feedFilter === "all") return feed;
    if (feedFilter === "pvp") return feed.filter(k => k.type === "pvp" && !k.m_bFriendlyFire);
    if (feedFilter === "bot") return feed.filter(k => k.type === "ai");
    if (feedFilter === "ff")  return feed.filter(k => k.m_bFriendlyFire);
    return feed;
  }, [feed, feedFilter]);

  // Derive current base control: last known faction per base name
  const currentBases = useMemo(() => {
    const map = {};
    bases.forEach(b => { map[b.m_sBaseName] = b.m_sFaction; });
    return map;
  }, [bases]);

  const baseFactionCount = useMemo(() => {
    const counts = { US: 0, USSR: 0, FIA: 0 };
    Object.values(currentBases).forEach(f => {
      if (f === "US" || f === "USSR" || f === "FIA") counts[f]++;
    });
    return counts;
  }, [currentBases]);

  if (error) {
    return (
      <div className="tac-panel corners p-6" style={{ borderColor: "var(--red)" }}>
        <span className="corner-tr" /><span className="corner-bl" />
        <div className="font-stencil text-lg mb-1" style={{ color: "var(--red-bright)" }}>SIGNAL LOST</div>
        <div className="text-sm font-mono-tech" style={{ color: "var(--text-muted)" }}>{error}</div>
      </div>
    );
  }

  if (!summary) {
    return <div className="font-mono-tech text-sm" style={{ color: "var(--text-muted)" }}>// LADE OPERATIONSDATEN …</div>;
  }

  return (
    <div className="space-y-6 relative z-[2]">
      <HeroBanner
        eyebrow="OPERATIONAL STATUS · CURRENT MISSION"
        title="AKTUELLE SESSION"
        meta={
          <>
            MISSION START: <span style={{ color: "var(--text)" }}>{sessionStarted}</span>
            {sessionDuration && (
              <>
                <span className="mx-2">|</span>
                RUNTIME: <span style={{ color: "var(--tan-bright)" }} className="font-mono-tech tabular-nums">{sessionDuration}</span>
              </>
            )}
            <span className="mx-2">|</span>
            INSTANCE <span style={{ color: "var(--tan-bright)" }}>{instance}</span>
          </>
        }
        badge={
          <span className="flex items-center gap-2 px-2.5 py-1 border" style={{
            borderColor: summary.sessionStart ? "var(--olive-bright)" : "var(--border)",
            background: summary.sessionStart ? "rgba(138, 165, 71, 0.12)" : "transparent",
          }}>
            <span className={`w-2 h-2 rounded-full ${summary.sessionStart ? "pulse-dot" : ""}`} style={{
              background: summary.sessionStart ? "var(--olive-bright)" : "var(--text-faint)",
            }} />
            <span className="label-tech" style={{ color: summary.sessionStart ? "var(--olive-bright)" : "var(--text-faint)" }}>
              {summary.sessionStart ? "LIVE" : "OFFLINE"}
            </span>
          </span>
        }
      />

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <StatTile icon={Users} label="PERSONNEL" value={summary.currentPlayers}
                  sub={`${summary.connects}↑ / ${summary.disconnects}↓`} />
        <StatTile icon={Crosshair} label="PVP KILLS" value={summary.killsTotal} accent="amber" />
        <StatTile icon={Bot} label="AI KILLS" value={summary.aiKills} accent="olive" />
        <StatTile icon={Shield} label="FRIENDLY FIRE" value={summary.friendlyFires} accent="red" />
        <StatTile icon={Skull} label="K.I.A. SELF" value={summary.suicides} accent="red" />
        <StatTile icon={Truck} label="VEH. DESTROYED" value={summary.vehiclesDestroyed}
                  sub={summary.vehicleTypes?.slice(0, 2).map((v) => cleanPrefab(v.type)).join(" · ") || "—"} />
      </div>

      {/* Killfeed + Chat */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CornerFrame className="tac-panel lg:col-span-2">
          <PanelHeader
            title="KILL FEED"
            icon={Crosshair}
            badge={`${filteredFeed.length} CONFIRMED`}
            right={
              <div className="flex items-center gap-0.5">
                {FEED_FILTERS.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setFeedFilter(f.id)}
                    className="label-tech px-2 py-0.5 text-[10px] transition-colors"
                    style={{
                      color: feedFilter === f.id ? "var(--tan-bright)" : "var(--text-faint)",
                      borderBottom: `1px solid ${feedFilter === f.id ? "var(--tan-bright)" : "transparent"}`,
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            }
          />
          <div className="divide-y max-h-[440px] overflow-y-auto" style={{ borderColor: "var(--border-faint)" }}>
            {filteredFeed.length === 0 && (
              <div className="p-6 text-center font-mono-tech text-xs" style={{ color: "var(--text-faint)" }}>NO ENGAGEMENTS LOGGED</div>
            )}
            {filteredFeed.map((k, idx) => {
              const isAI = k.type === "ai";
              const victimLabel = isAI
                ? cleanPrefab(k.m_sVictimType || "AI")
                : k.m_sVictimName;
              return (
                <div key={idx} className="px-3 py-2 flex items-center gap-2.5 text-sm hover:bg-[var(--surface-2)]" style={{ borderColor: "var(--border-faint)" }}>
                  <span className="font-mono-tech text-[11px] w-[60px] shrink-0" style={{ color: "var(--tan)" }}>{formatTime(k.m_sTimestamp)}</span>
                  <FactionPatch faction={k.m_sKillerFaction} />
                  <span className={`truncate font-medium ${k.m_bFriendlyFire ? "text-[var(--red-bright)]" : ""}`}>{k.m_sKillerName}</span>
                  <Crosshair className="w-3 h-3 shrink-0" style={{ color: "var(--text-faint)" }} />
                  {isAI
                    ? <span className="label-tech px-1 text-[10px] shrink-0" style={{ color: "var(--amber)", border: "1px solid var(--amber)" }}>BOT</span>
                    : <FactionPatch faction={k.m_sVictimFaction} />
                  }
                  <span className="truncate" style={{ color: isAI ? "var(--amber)" : "var(--text-muted)" }}>{victimLabel}</span>
                  <span className="ml-auto flex items-center gap-2 shrink-0">
                    {!isAI && k.m_bSuicide && <span className="label-tech px-1" style={{ color: "var(--text-faint)", border: "1px solid var(--border-bright)" }}>SUI</span>}
                    {!isAI && k.m_bFriendlyFire && <span className="label-tech px-1" style={{ color: "var(--red-bright)", border: "1px solid var(--red)" }}>FF</span>}
                    <span className="text-[10px] font-mono-tech" style={{ color: "var(--text-muted)" }}>{cleanPrefab(k.m_sWeaponName)}</span>
                    <span className="text-[10px] font-mono-tech tabular-nums w-12 text-right" style={{ color: "var(--amber-bright)" }}>
                      {Math.round(k.m_fKillDistance)}M
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </CornerFrame>

        <CornerFrame className="tac-panel flex flex-col">
          <PanelHeader title="COMMS / CHAT" icon={MessageSquare} badge={chat.length} />
          <div className="flex-1 overflow-y-auto max-h-[440px] px-3 py-2 space-y-1 font-mono-tech text-[11px]">
            {chat.length === 0 && (
              <div className="text-center py-6" style={{ color: "var(--text-faint)" }}>RADIO SILENCE</div>
            )}
            {chat.map((c, i) => (
              <div key={i}>
                <span style={{ color: "var(--text-faint)" }}>[{formatTime(c.m_sTimestamp)}]</span>{" "}
                <span style={{ color: "var(--tan-bright)" }}>{c.m_sPlayerName}:</span>{" "}
                <span style={{ color: "var(--text)" }}>{c.m_sMessage}</span>
              </div>
            ))}
          </div>
        </CornerFrame>
      </div>

      {/* Bases + Vehicles */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CornerFrame className="tac-panel">
          <PanelHeader title="OBJECTIVES SECURED" icon={Flag} badge={`${Object.keys(currentBases).length} BASES`} />
          {Object.keys(currentBases).length > 0 && (
            <div className="px-3 py-2 flex items-center gap-4 border-b" style={{ borderColor: "var(--border-faint)", background: "rgba(0,0,0,0.18)" }}>
              <span className="label-tech text-[10px]" style={{ color: "var(--text-faint)" }}>CONTROL</span>
              {baseFactionCount.US > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="patch patch-us">US</span>
                  <span className="font-mono-tech text-xs font-bold" style={{ color: "var(--tan-bright)" }}>{baseFactionCount.US}</span>
                </span>
              )}
              {baseFactionCount.USSR > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="patch patch-ussr">USSR</span>
                  <span className="font-mono-tech text-xs font-bold" style={{ color: "var(--red-bright)" }}>{baseFactionCount.USSR}</span>
                </span>
              )}
              {baseFactionCount.FIA > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="patch patch-fia">FIA</span>
                  <span className="font-mono-tech text-xs font-bold" style={{ color: "var(--olive-bright)" }}>{baseFactionCount.FIA}</span>
                </span>
              )}
              {baseFactionCount.US > 0 && baseFactionCount.USSR > 0 && (() => {
                const total = baseFactionCount.US + baseFactionCount.USSR + baseFactionCount.FIA;
                const usPct = Math.round((baseFactionCount.US / total) * 100);
                return (
                  <div className="flex-1 h-1.5 rounded-none overflow-hidden ml-2" style={{ background: "var(--surface-2)" }}>
                    <div className="h-full" style={{ width: `${usPct}%`, background: "var(--tan-bright)", transition: "width 0.6s ease" }} />
                  </div>
                );
              })()}
            </div>
          )}
          <div className="divide-y" style={{ borderColor: "var(--border-faint)" }}>
            {bases.length === 0 && (
              <div className="p-6 text-center font-mono-tech text-xs" style={{ color: "var(--text-faint)" }}>NO OBJECTIVES TAKEN</div>
            )}
            {[...bases].reverse().map((b, i) => (
              <div key={i} className="px-3 py-2 flex items-center gap-3 text-sm" style={{ borderColor: "var(--border-faint)" }}>
                <span className="font-mono-tech text-[11px] w-[60px]" style={{ color: "var(--tan)" }}>{formatTime(b.m_sTimestamp)}</span>
                <Flag className="w-3 h-3" style={{ color: "var(--olive-bright)" }} />
                <span className="font-display tracking-wide">{b.m_sBaseName}</span>
                <span className="ml-auto flex items-center gap-2">
                  <FactionPatch faction={b.m_sPreviousFaction} />
                  <span style={{ color: "var(--text-faint)" }}>→</span>
                  <FactionPatch faction={b.m_sFaction} />
                </span>
              </div>
            ))}
          </div>
        </CornerFrame>

        <CornerFrame className="tac-panel">
          <PanelHeader title="VEHICLES DESTROYED" icon={Truck} badge={vehicles.length} />
          <div className="divide-y max-h-[320px] overflow-y-auto" style={{ borderColor: "var(--border-faint)" }}>
            {vehicles.length === 0 && (
              <div className="p-6 text-center font-mono-tech text-xs" style={{ color: "var(--text-faint)" }}>NONE</div>
            )}
            {vehicles.map((v, i) => (
              <div key={i} className="px-3 py-2 flex items-center gap-2.5 text-sm" style={{ borderColor: "var(--border-faint)" }}>
                <span className="font-mono-tech text-[11px] w-[60px]" style={{ color: "var(--tan)" }}>{formatTime(v.m_sTimestamp)}</span>
                <FactionPatch faction={v.m_sVehicleFaction} />
                <span className="font-medium truncate">{cleanPrefab(v.m_sVehicleType)}</span>
                <span className="ml-auto flex items-center gap-2 text-[10px] font-mono-tech" style={{ color: "var(--text-muted)" }}>
                  <Radio className="w-3 h-3" />
                  <span>{v.m_sKillerName}</span>
                  <span style={{ color: "var(--amber-bright)" }}>{Math.round(v.m_fKillDistance)}M</span>
                </span>
              </div>
            ))}
          </div>
        </CornerFrame>
      </div>
    </div>
  );
}
